import { useState, type FormEvent } from 'react';
import { patchMyTutorProfile, upsertMyTutorProfile } from '@/lib/tutor-api';
import { Field, FooterButtons, type StepProps, withCompletedStep } from './shared';

export function IdentityStep({
  profile,
  accessToken,
  firstName,
  lastName,
  onSaved,
  onError,
}: StepProps & { firstName: string; lastName: string }) {
  const initialDisplay = profile?.displayName || `${firstName} ${lastName}`;
  const [displayName, setDisplayName] = useState(initialDisplay);
  const [headline, setHeadline] = useState(profile?.headline ?? '');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fn = profile ? patchMyTutorProfile : upsertMyTutorProfile;
      const next = await fn(accessToken, {
        displayName,
        headline,
        completedSteps: withCompletedStep(profile, 'identity'),
      });
      onSaved(next);
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  const initials = displayName.trim()
    ? displayName.trim().split(/\s+/).map((w) => w[0] ?? '').slice(0, 2).join('').toUpperCase()
    : (firstName[0] ?? '').toUpperCase();

  return (
    <form className="space-y-5" onSubmit={submit}>
      <p className="text-sm text-muted-foreground">
        Signed in as <strong className="font-semibold text-foreground">{firstName} {lastName}</strong>.
        Choose a display name parents will see — most tutors use their first name + last initial.
      </p>

      {/* Live preview card */}
      <div className="rounded-xl border border-brand-100 bg-brand-50/60 p-4 dark:border-slate-700 dark:bg-slate-800/60">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-400">
          Preview — how parents see you
        </p>
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-600 text-base font-bold text-white shadow-sm">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-foreground">
              {displayName.trim() || <span className="italic text-muted-foreground">Your display name</span>}
            </p>
            <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
              {headline.trim() || <span className="italic">Your headline will appear here</span>}
            </p>
          </div>
        </div>
      </div>

      <Field
        id="displayName"
        label="Display name"
        value={displayName}
        onChange={setDisplayName}
        maxLength={120}
        required
        placeholder={`e.g. ${firstName} ${lastName}`}
      />
      <Field
        id="headline"
        label="One-line headline"
        helper={"What's your pitch? e.g. \"Math + Science · 10 yrs · CBSE/IB\""}
        value={headline}
        onChange={setHeadline}
        maxLength={160}
        required
        placeholder="Your subject expertise and style in one sentence"
      />
      <FooterButtons primaryLabel="Save & continue" busy={busy} />
    </form>
  );
}
